import { HeaderIcons } from '../shared/icons';
import React, { useState, useEffect, useRef } from 'react';
import { WebSocketAdapter } from '../../utils/WebSocketAdapter';
import Header from '../shared/Header';
import { StandingsSidebar, LoadingQuestion, TimerSection, WaitingSpinner } from '../shared';
import { GameEndScreen } from './GameEndScreen';
import { getGameName, calculateTimeRemaining } from '../../utils/gameUtils';
import { useGameEndScreenData } from '../../hooks/useGameEndScreenData';
import { gameUsesTeams } from '../../utils/teamGames';
import { CatchShoutCatchHostFalling } from './catchShoutCatch';
import type { GameStatistics, Question } from '../../types/game';
import '../../App.css';
import '../../styles/game-layout.css';
import '../../styles/game-screen.css';
import { useTranslation } from '../../i18n';

interface HostQuestion {
  questionId?: string;
  id?: string;
  question: string;
  title?: string;
  timeLimit: number;
  questionNumber: number;
  totalQuestions: number;
  startTime: number;
  isCatchShoutCatch?: boolean;
  themeTitle?: string;
  animals?: string[];
}

interface CatchShoutCatchHostDisplayProps {
  roomState: {
    roomName: string;
    gameType: string;
    hostId: string;
    players: Array<{ id: string; name: string; score: number; avatar?: string; teamId?: 'A' | 'B' }>;
    teams?: { A: { id: 'A'; name: string; score: number }; B: { id: 'B'; name: string; score: number } };
    gameState: 'waiting' | 'playing' | 'finished';
    currentQuestion?: HostQuestion;
    questionStartTime?: number;
  };
  socket: WebSocketAdapter;
  onBackToGameSelection?: () => void;
  myPlayerId?: string;
  hostIsPlaying?: boolean;
  onGoHome?: () => void;
  onLoadingTimeout?: () => void;
}

const CatchShoutCatchHostDisplay: React.FC<CatchShoutCatchHostDisplayProps> = ({
  roomState,
  socket,
  onBackToGameSelection,
  hostIsPlaying = false,
  onGoHome,
  onLoadingTimeout,
}) => {
  const { t } = useTranslation();
  const [currentQuestion, setCurrentQuestion] = useState<HostQuestion | null>(
    roomState.currentQuestion || null
  );
  const [timeRemaining, setTimeRemaining] = useState(roomState.currentQuestion?.timeLimit ?? 0);
  const [gameEnded, setGameEnded] = useState(false);
  const [finalLeaderboard, setFinalLeaderboard] = useState<Array<{ playerId: string; playerName: string; score: number }>>([]);
  const [gameStatistics, setGameStatistics] = useState<GameStatistics | null>(null);
  const [roundCatches, setRoundCatches] = useState<{ A: number; B: number }>({ A: 0, B: 0 });
  const previousQuestionIdRef = useRef<string | undefined>(undefined);
  const gameEndData = useGameEndScreenData(roomState);

  useEffect(() => {
    if (roomState.gameState === 'finished' && gameEndData) {
      setGameEnded(true);
      setCurrentQuestion(null);
      setFinalLeaderboard(gameEndData.finalLeaderboard);
      setGameStatistics(gameEndData.gameStatistics);
    } else if (roomState.gameState === 'playing' && gameEnded) {
      setGameEnded(false);
      setCurrentQuestion(null);
      setFinalLeaderboard([]);
      setGameStatistics(null);
      previousQuestionIdRef.current = undefined;
      setRoundCatches({ A: 0, B: 0 });
    }
  }, [roomState.gameState, gameEndData, gameEnded]);

  useEffect(() => {
    const handleQuestion = (data: HostQuestion & { questionId: string }) => {
      const qId = data.questionId || data.id;
      if (qId && qId === previousQuestionIdRef.current) return;
      previousQuestionIdRef.current = qId;
      setCurrentQuestion({ ...data, questionId: qId });
      setTimeRemaining(data.timeLimit);
      setRoundCatches({ A: 0, B: 0 });
    };

    const handleRoundScore = (data: {
      questionId: string;
      roundCatches: { A: number; B: number };
    }) => {
      const qId = currentQuestion?.questionId || currentQuestion?.id;
      if (data.questionId !== qId) return;
      setRoundCatches(data.roundCatches);
    };

    const handleGameEnded = (data: { leaderboard?: typeof finalLeaderboard; statistics?: GameStatistics }) => {
      setGameEnded(true);
      setCurrentQuestion(null);
      setFinalLeaderboard(data.leaderboard || []);
      setGameStatistics(data.statistics || null);
    };

    const handleGameStarted = () => {
      setGameEnded(false);
      setCurrentQuestion(null);
      setFinalLeaderboard([]);
      setGameStatistics(null);
      previousQuestionIdRef.current = undefined;
      setRoundCatches({ A: 0, B: 0 });
    };

    socket.on('question', handleQuestion);
    socket.on('catch-shout-catch-round-score', handleRoundScore);
    socket.on('game-ended', handleGameEnded);
    socket.on('game-started', handleGameStarted);

    return () => {
      socket.off('question', handleQuestion);
      socket.off('catch-shout-catch-round-score', handleRoundScore);
      socket.off('game-ended', handleGameEnded);
      socket.off('game-started', handleGameStarted);
    };
  }, [socket, currentQuestion]);

  useEffect(() => {
    if (roomState.currentQuestion) {
      const qId = roomState.currentQuestion.questionId || roomState.currentQuestion.id;
      if (qId !== previousQuestionIdRef.current) {
        previousQuestionIdRef.current = qId;
        setCurrentQuestion(roomState.currentQuestion);
        setTimeRemaining(
          calculateTimeRemaining(
            roomState.currentQuestion as Question,
            roomState.questionStartTime || roomState.currentQuestion.startTime
          )
        );
        setRoundCatches({ A: 0, B: 0 });
      }
    }
  }, [roomState.currentQuestion, roomState.questionStartTime]);

  useEffect(() => {
    if (!currentQuestion || gameEnded) return;
    const interval = setInterval(() => {
      const start = roomState.questionStartTime || currentQuestion.startTime;
      const remaining = Math.max(0, currentQuestion.timeLimit - (Date.now() - start));
      setTimeRemaining(remaining);
    }, 100);
    return () => clearInterval(interval);
  }, [currentQuestion, gameEnded, roomState.questionStartTime]);

  if (gameEnded) {
    const teamLeaderboard = roomState.teams
      ? [
          { playerId: 'A', playerName: roomState.teams.A.name, score: roomState.teams.A.score },
          { playerId: 'B', playerName: roomState.teams.B.name, score: roomState.teams.B.score },
        ].sort((a, b) => b.score - a.score)
      : finalLeaderboard;

    return (
      <GameEndScreen
        finalLeaderboard={teamLeaderboard}
        gameStatistics={gameStatistics}
        onPlaySameGame={() => {
          socket.emit('start-game', { roomName: roomState.roomName, gameType: roomState.gameType });
          setGameEnded(false);
        }}
        onPlayAnotherGame={onBackToGameSelection || (() => {})}
        onBackToGameSelection={onBackToGameSelection}
        roomName={roomState.roomName}
        gameType={roomState.gameType}
      />
    );
  }

  if (!currentQuestion) {
    if (roomState.gameState === 'playing') {
      return (
        <LoadingQuestion
          socket={socket}
          roomName={roomState.roomName}
          title={t('gameplay.loading.question')}
          onGoHome={onGoHome}
          onLongWaitTimeout={onLoadingTimeout}
        />
      );
    }
    return (
      <div className="app">
        <Header roomCode={roomState.roomName} />
        <WaitingSpinner message={t('gameplay.catchShoutCatch.waiting')} isLoading />
      </div>
    );
  }

  const timeSeconds = Math.ceil(timeRemaining / 1000);
  const progress = currentQuestion.timeLimit > 0 ? timeRemaining / currentQuestion.timeLimit : 0;
  const themeTitle = currentQuestion.themeTitle || currentQuestion.title || t('gameplay.catchShoutCatch.hostTitle');
  const animals = currentQuestion.animals ?? [];
  const roundActive = timeRemaining > 0;

  return (
    <div className="app">
      <Header
        leftButton={
          onBackToGameSelection
            ? {
                icon: HeaderIcons.back,
                onClick: onBackToGameSelection,
                tooltip: t('gameplay.backToGameSelection'),
              }
            : undefined
        }
        rightButton={{
          icon: HeaderIcons.refresh,
          onClick: () => window.location.reload(),
          tooltip: t('common.tooltips.refreshPage'),
        }}
        centerContent={
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '1.5rem', flexWrap: 'wrap' }}>
            <div className="header-game-name" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span className="game-header-label">{t('gameplay.partyHost.gameLabel')}</span>
              <span className="game-header-value">{getGameName(roomState.gameType)}</span>
            </div>
            <div className="game-header-divider header-game-name" />
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span className="game-header-label">{t('gameplay.partyHost.roomCodeLabel')}</span>
              <div
                style={{
                  fontSize: '1.3rem',
                  fontWeight: 'bold',
                  letterSpacing: '0.15rem',
                  color: 'var(--color-primary)',
                  padding: '0.3rem 0.8rem',
                  backgroundColor: 'rgba(var(--color-primary-rgb), 0.1)',
                  borderRadius: '6px',
                  border: '2px solid var(--color-primary)',
                }}
              >
                {roomState.roomName}
              </div>
            </div>
          </div>
        }
      />

      <div className="game-host-layout">
        <div className={`game-host-left no-image ${!hostIsPlaying ? 'host-only' : ''}`}>
          <div className="game-host-timer-section">
            <TimerSection
              questionNumber={currentQuestion.questionNumber}
              totalQuestions={currentQuestion.totalQuestions}
              timeSeconds={timeSeconds}
              progress={progress}
            />
          </div>

          <div className="game-host-question-section" style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
            <CatchShoutCatchHostFalling
              themeTitle={themeTitle}
              animals={animals}
              active={roundActive}
              roundCatches={roundCatches}
            />
          </div>
        </div>

        <div className="game-host-right">
          <StandingsSidebar
            players={roomState.players}
            liveAnswers={[]}
            totalPlayers={roomState.players.length}
            questionId={currentQuestion.questionId || currentQuestion.id}
            mode={gameUsesTeams(roomState.gameType) ? 'teams' : 'players'}
            teams={roomState.teams}
            shouldUpdateScores
          />
        </div>
      </div>
    </div>
  );
};

export default CatchShoutCatchHostDisplay;
